import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ThemeName = 'professional' | 'bold' | 'playful' | 'muted'
export type ColorMode = 'light' | 'dark'

export interface ThemeInfo {
    name: ThemeName
    label: string
    description: string
}

export const THEMES: ThemeInfo[] = [
    { name: 'professional', label: 'Professional', description: 'Clean and corporate' },
    { name: 'bold', label: 'Bold', description: 'High-contrast and vibrant' },
    { name: 'playful', label: 'Playful', description: 'Warm and approachable' },
    { name: 'muted', label: 'Muted', description: 'Desaturated and minimal' },
]

interface ThemeState {
    /** User's chosen theme (null = use site default) */
    theme: ThemeName | null
    /** User's chosen color mode (null = use site default) */
    colorMode: ColorMode | null
    /** Site-wide defaults loaded from the API */
    siteDefaultTheme: ThemeName
    siteDefaultColorMode: ColorMode

    setTheme: (theme: ThemeName | null) => void
    setColorMode: (mode: ColorMode | null) => void
    setSiteDefaults: (theme: ThemeName, colorMode: ColorMode) => void
    /** Returns the effective theme (user override or site default) */
    getEffectiveTheme: () => ThemeName
    getEffectiveColorMode: () => ColorMode
    /** Applies the current theme + color mode to the DOM */
    applyTheme: () => void
}

export const useThemeStore = create<ThemeState>()(
    persist(
        (set, get) => ({
            theme: null,
            colorMode: null,
            siteDefaultTheme: 'professional',
            siteDefaultColorMode: 'dark',

            setTheme: (theme) => {
                set({ theme })
                get().applyTheme()
            },

            setColorMode: (mode) => {
                set({ colorMode: mode })
                get().applyTheme()
            },

            setSiteDefaults: (theme, colorMode) => {
                set({ siteDefaultTheme: theme, siteDefaultColorMode: colorMode })
                get().applyTheme()
            },

            getEffectiveTheme: () => {
                const { theme, siteDefaultTheme } = get()
                return theme ?? siteDefaultTheme
            },

            getEffectiveColorMode: () => {
                const { colorMode, siteDefaultColorMode } = get()
                return colorMode ?? siteDefaultColorMode
            },

            applyTheme: () => {
                const state = get()
                const effectiveTheme = state.theme ?? state.siteDefaultTheme
                const effectiveMode = state.colorMode ?? state.siteDefaultColorMode
                document.documentElement.setAttribute('data-theme', effectiveTheme)
                document.documentElement.setAttribute('data-color-mode', effectiveMode)
            },
        }),
        {
            name: 'puresoft-theme',
            partialize: (state) => ({
                theme: state.theme,
                colorMode: state.colorMode,
            }),
        }
    )
)
